import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { makeHandlerChainAwaitable } from "@insite/client-framework/HandlerCreator";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import validateReCaptcha from "@insite/client-framework/Store/Components/ReCaptcha/Handlers/ValidateReCaptcha";
import {
    getCountriesDataView,
    getCurrentCountries,
} from "@insite/client-framework/Store/Data/Countries/CountriesSelectors";

import loadCurrentCountries, {
    loadCurrentCountriesParameter,
} from "@insite/client-framework/Store/Data/Countries/Handlers/LoadCurrentCountries";
import translate from "@insite/client-framework/Translate";
import { StateModel } from "@insite/client-framework/Types/ApiModels";
import { HasFields } from "@insite/client-framework/Types/ContentItemModel";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import ReCaptcha from "@insite/content-library/Components/ReCaptcha";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Modal, { ModalPresentationProps } from "@insite/mobius/Modal";
import Select, { SelectPresentationProps } from "@insite/mobius/Select";
import TextArea, { TextAreaPresentationProps } from "@insite/mobius/TextArea/TextArea";
import TextField, { TextFieldProps } from "@insite/mobius/TextField";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import getProp from "@insite/mobius/utilities/getProp";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { formatPhoneNumber } from "@tCentro/Common/Utilities/formatPhoneNumber";
import createInquiryLineCardForm from "@tCentro/Handlers/CENInquiryLineCardFormHandler";
import { COLORS, TYPOGRAPHY } from "@tCentro/theme/design";
import React, { RefObject, useEffect, useState } from "react";
import ReCAPTCHA from "react-google-recaptcha";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const enum fields {
    submitButtonText = "submitButtonText",
    description = "description",
    successMessage = "successMessage",
    requestypes = "requestypes",
    optionName = "optionName",
}

interface RequestTypeModel {
    fields: {
        optionName: string;
    };
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.submitButtonText]: string;
        [fields.description]: string;
        [fields.successMessage]: string;
        [fields.requestypes]: RequestTypeModel[];
    };
}
const mapStateToProps = (state: ApplicationState) => ({
    countries: getCurrentCountries(state),
    countriesDataView: getCountriesDataView(state, loadCurrentCountriesParameter).isLoading,
});

const mapDispatchToProps = {
    validateReCaptcha: makeHandlerChainAwaitable<{ location: string; recaptchaRef: React.RefObject<ReCAPTCHA | null> }, boolean>(
        validateReCaptcha,
    ),
    loadCurrentCountries,
    createInquiryLineCardForm,
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface CENHelpPlacingOrderFormStyles {
    container?: GridContainerProps;
    successModal?: ModalPresentationProps;
    messageTextGridItem?: GridItemProps;
    messageText?: TypographyPresentationProps;
    continueButton?: ButtonPresentationProps;
    buttonGridItem?: GridItemProps;
    modalContainer?: GridContainerProps;
    mainContainer: InjectableCss;
    inputStyles?: TextFieldProps;
    submitButton?: InjectableCss;
    textAreaStyles?: TextAreaPresentationProps;
    dFlexRowStyles?: InjectableCss;
    selectStyles?: SelectPresentationProps;
    captchaStyles?: InjectableCss;
}

export const cenHelpPlacingOrderFormStyles: CENHelpPlacingOrderFormStyles = {
    captchaStyles: {
        css: css`
            margin-top: 24px;
        `,
    },
    successModal: { sizeVariant: "small" },
    messageTextGridItem: { width: 12 },
    buttonGridItem: {
        width: 12,
        css: css`
            justify-content: flex-end;
        `,
    },
    dFlexRowStyles: {
        css: css`
            display: flex;
            gap: 16px;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        flex-direction: column;
                    `,
                    css`
                        flex-direction: column;
                    `,
                    css`
                        flex-direction: column;
                    `,
                    null,
                    null,
                ])}
        `,
    },
    selectStyles: {
        border: "rounded",
        errorProps: {
            css: css`
                margin-left: 24px;
            `,
        },
        cssOverrides: {
            formField: css`
                padding-top: 18px !important;
                > label {
                    font-size: 14px;
                    font-weight: 600;
                    line-height: 16px;
                    color: ${getProp("theme.typography.p.color")};
                    margin-left: 24px;
                }
            `,

            formInputWrapper: css`
                select {
                    border: 2px solid #f2f2f2 !important;
                    border-radius: 40px !important;
                    min-height: 48px !important;
                    background-color: #f2f2f2 !important;
                    padding-left: 20px !important;
                }
            `,
        },
    },
    inputStyles: {
        errorProps: {
            css: css`
                margin-left: 24px;
            `,
        },
        cssOverrides: {
            formField: css`
                padding-top: 24px;
                label {
                    font-size: ${TYPOGRAPHY.size.smaller};
                    font-weight: ${TYPOGRAPHY.weight.mediumbold};
                    line-height: 16px;
                    color: ${getProp("theme.typography.p.color")};
                    margin-left: 24px;
                    min-height: auto !important;
                }
            `,
            formInputWrapper: css`
                margin-top: 8px !important;
                input {
                    background: #f2f2f2 !important;
                    border-radius: 40px !important;
                    padding: 12px 24px !important;
                    min-height: 48px !important;
                    border: none !important;
                    &::placeholder {
                        font-size: 14px;
                        font-style: normal;
                        font-weight: 400;
                        line-height: 24px;
                    }
                }
            `,
        },
    },

    textAreaStyles: {
        css: css`
            background: #f2f2f2 !important;
            border-radius: 40px !important;
            padding: 20px !important;
        `,
        labelProps: {
            css: css`
                margin-top: 24px;
                margin-left: 24px;
                color: ${getProp("theme.typography.p.color")};
            `,
        },
        errorProps: {
            css: css`
                margin-left: 24px;
            `,
        },
    },
    mainContainer: {
        css: css`
            padding-left: 34px;
            border-left: 2px solid #f2f2f2;
            height: 100%;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        padding-left: 0px;
                        border: none;
                    `,
                    css`
                        padding-left: 0px;
                        border: none;
                    `,
                    null,
                    null,
                    null,
                ])}
        `,
    },
    submitButton: {
        css: css`
            margin-top: 24px;
            border-radius: 24px;
        `,
    },
};

const styles = cenHelpPlacingOrderFormStyles;

const emailsRegexp = new RegExp(
    "^[\\W]*([\\w+\\-.%]+@[\\w\\-.]+\\.[A-Za-z]{2,4}[\\W]*,{1}[\\W]*)*([\\w+\\-.%]+@[\\w\\-.]+\\.[A-Za-z]{2,4})[\\W]*$",
);
const emailRegexp = new RegExp("\\w+([-+.']\\w+)*@\\w+([-.]\\w+)*\\.(?!usa$)\\w+([-.]\\w+)*", "i");

// const phoneRegex = /^\d{3}(-\d{3})?(-\d{4})?$/;
const phoneRegex = /^(\d{3}-){2}\d{4}$/;

const CENInquiryLineCard = ({
    id,
    fields,
    countries,
    countriesDataView,
    loadCurrentCountries,
    createInquiryLineCardForm,
    validateReCaptcha,
}: Props) => {
    useEffect(() => {
        if (!countries) {
            loadCurrentCountries();
        }
    }, []);

    const selectedCountry = countries?.find(c => c.abbreviation === "US");

    useEffect(() => {
        setStates(selectedCountry?.states || []);
    }, [selectedCountry]);

    const [fullname, setFullname] = useState("");
    const [companyName, setCompanyName] = useState("");
    const [emailAddress, setEmailAddress] = useState("");
    const [message, setMessage] = useState("");
    const [city, setCity] = useState("");
    const [zipCode, setZipCode] = useState("");
    const [websiteName, setWebsiteName] = useState("");
    const [streetAddress, setStreetAddress] = useState("");
    const [phoneNumber, setPhoneNumber] = useState("");
    const [countryState, setCountryState] = useState<any>();
    const [requestType, setRequestType] = useState("");

    const fullnameRequiredFieldMessage = siteMessage("inquiryLineCard_fullnameRequiredFieldMessage");
    const emailAddressRequiredFieldMessage = siteMessage("inquiryLineCard_emailAddressRequiredFieldMessage");
    const invalidEmailAddressMessage = siteMessage("inquiryLineCard_invalidEmailAddressMessage");
    const messageRequiredFieldMessage = siteMessage("inquiryLineCard_messageRequiredFieldMessage");
    const cityRequiredFieldMessage = siteMessage("inquiryLineCard_cityRequiredFieldMessage");
    const zipCodeRequiredFieldMessage = siteMessage("inquiryLineCard_zipCodeRequiredFieldMessage");
    const companyRequiredFieldMessage = siteMessage("inquiryLineCard_companyRequiredFieldMessage");
    const phoneNumberRequiredFieldMessage = siteMessage("inquiryLineCard_phoneNumberRequiredFieldMessage");
    const invalidPhoneNumberFieldMessage = siteMessage("inquiryLineCard_invalidPhoneNumberFieldMessage");
    const stateRequiredFieldMessage = siteMessage("inquiryLineCard_stateRequiredFieldMessage");
    const requestTypeRequiredFieldMessage = siteMessage("inquiryLineCard_requestTypeRequiredFieldMessage");

    const [emailAddressError, setEmailAddressError] = useState(emailAddressRequiredFieldMessage);
    const [cityError, setCityError] = useState(cityRequiredFieldMessage);
    const [fullnameError, setFullnameError] = useState(fullnameRequiredFieldMessage);
    const [messageError, setMessageError] = useState(messageRequiredFieldMessage);
    const [zipCodeError, setZipCodeError] = useState(zipCodeRequiredFieldMessage);
    const [companyError, setCompanyError] = useState(companyRequiredFieldMessage);
    const [phoneNumberError, setPhoneNumberError] = useState(phoneNumberRequiredFieldMessage);
    const [stateError, setStateError] = useState(stateRequiredFieldMessage);
    const [requestTypeError, setRequestTypeError] = useState(requestTypeRequiredFieldMessage);
    const [invalidEmailAddressError, setInvalidEmailAddressError] = useState(invalidEmailAddressMessage);
    const [invalidPhoneNumberError, setInvalidPhoneNumberError] = useState(invalidPhoneNumberFieldMessage);

    const [isSubmitted, setIsSubmitted] = useState(false);
    const [isButtonDisabled, setIsButtonDisabled] = React.useState(true);
    const [states, setStates] = useState<StateModel[]>([]);
    const [isModalOpen, setIsModalOpen] = useState(false);

    const recaptchaRef: React.RefObject<ReCAPTCHA | null> = React.createRef();

    const fullnameChangeHandler = (event: React.ChangeEvent<HTMLInputElement>) => {
        setFullname(event.target.value);
        validateFullNameHandler(event.target.value);
    };

    const validateFullNameHandler = (fullname: string) => {
        const errorMessage = !fullname ? fullnameRequiredFieldMessage : "";
        setFullnameError(errorMessage);
        return !errorMessage;
    };

    const emailChangeHandler = (event: React.ChangeEvent<HTMLInputElement>) => {
        setEmailAddress(event.target.value);
        validateEmailAddressHandler(event.target.value);
    };

    const validateEmailAddressHandler = (emailAddress: string) => {
        let errorMessage = !emailAddress ? emailAddressRequiredFieldMessage : "";
        if (emailAddress && !emailRegexp.test(emailAddress)) {
            errorMessage = invalidEmailAddressMessage;
        }

        setEmailAddressError(errorMessage);
        return !errorMessage;
    };

    const streetAddressChangeHandler = (e: React.ChangeEvent<HTMLInputElement>) => {
        setStreetAddress(e.target.value);
    };

    const websiteNameChangeHander = (e: React.ChangeEvent<HTMLInputElement>) => {
        setWebsiteName(e.target.value);
    };

    const phoneNumberChangeHandler = (event: React.ChangeEvent<HTMLInputElement>) => {
        setPhoneNumber(formatPhoneNumber(event.target.value));
        validatePhoneNumberHandler(event.target.value);
    };

    const validatePhoneNumberHandler = (phoneNumber: any) => {
        const errorMessage = !phoneNumber ? phoneNumberRequiredFieldMessage : "";

        setPhoneNumberError(errorMessage);
        return !errorMessage;
    };

    const companyHandler = (e: any) => {
        validateCompanyHandler(e.target.value);
        setCompanyName(e.target.value);
    };

    const validateCompanyHandler = (companyName: string) => {
        const errorMessage = !companyName ? companyRequiredFieldMessage : "";
        setCompanyError(errorMessage);
        return !errorMessage;
    };

    const stateChangeHandler = (stateValue: string) => {
        setCountryState(stateValue);
        validateState(stateValue);
    };

    const validateState = (stateValue: string) => {
        const errorMessage = !stateValue ? stateRequiredFieldMessage : "";
        setStateError(errorMessage);
        return !errorMessage;
    };

    const requestTypeHandler = (event: React.ChangeEvent<HTMLSelectElement>) => {
        setRequestType(event.target.value);
        validateRequestTypeHandler(event.target.value);
    };

    const validateRequestTypeHandler = (requestType: string) => {
        const errorMessage = requestType === "" ? requestTypeRequiredFieldMessage : "";
        setRequestTypeError(errorMessage);
        return !errorMessage;
    };

    const cityHandler = (e: any) => {
        validateCity(e.target.value);
        setCity(e.target.value);
    };

    const validateCity = (city: string) => {
        const errorMessage = !city ? cityRequiredFieldMessage : "";
        setCityError(errorMessage);
        return !errorMessage;
    };

    const zipCodeHandler = (e: React.ChangeEvent<HTMLInputElement>) => {
        validateZipCode(e.target.value);
        setZipCode(e.target.value);
    };

    const validateZipCode = (zipCode: string) => {
        const errorMessage = !zipCode ? zipCodeRequiredFieldMessage : "";
        setZipCodeError(errorMessage);
        return !errorMessage;
    };

    const messageChangeHandler = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
        setMessage(event.target.value);
        validateMessageHandler(event.target.value);
    };

    const validateMessageHandler = (message: string) => {
        const errorMessage = !message ? messageRequiredFieldMessage : "";
        setMessageError(errorMessage);
        return !errorMessage;
    };

    const validateSubmitEnabled = () => {
        return !(
            fullnameError ||
            messageError ||
            emailAddressError ||
            zipCodeError ||
            companyError ||
            cityError ||
            phoneNumberError ||
            stateError ||
            requestTypeError
        );
    };

    const isSubmitEnabled = validateSubmitEnabled();

    const submitFormHandler = async (event: React.MouseEvent<HTMLButtonElement>) => {
        event.preventDefault();

        const isReCaptchaValid = await validateReCaptcha({ location: "ContactUs", recaptchaRef });

        if (!isReCaptchaValid) {
            return;
        }

        function getDataLayer() {
            return (window as any).dataLayer;
        }

        const dataLayer = getDataLayer();
        if (dataLayer) {
            dataLayer.push({
                event: `CenInquiryLineCard`,
            });
        }
        setIsButtonDisabled(true);

        if (!isSubmitted) {
            setIsSubmitted(true);
        }

        if (!validateSubmitEnabled()) {
            // eslint-disable-next-line no-useless-return
            return;
        }

        createInquiryLineCardForm({
            Name: fullname,
            CompanyName: companyName,
            Email: emailAddress,
            Website: websiteName,
            PhoneNo: phoneNumber,
            Address: streetAddress,
            City: city,
            State: countryState,
            ZipCode: zipCode,
            RequestType: requestType,
            Message: message,
            onSuccess: () => {
                setIsModalOpen(true);
            },
            onComplete() {
                // "this" is targeting the object being created, not the parent SFC
                // eslint-disable-next-line react/no-this-in-sfc
                this.onSuccess?.();
            },
        });
    };

    const emptyFields = () => {
        setCompanyName("");
        setEmailAddress("");
        setFullname("");
        setMessage("");
        setCity("");
        setCountryState("");
        setPhoneNumber("");
        setZipCode("");
        setRequestType("");
        setWebsiteName("");
        setStreetAddress("");
    };

    const modalCloseHandler = () => setIsModalOpen(false);

    const continueButtonClickHandler = () => {
        setIsModalOpen(false);
        emptyFields();
        window.location.href = "/";
    };

    return (
        <StyledWrapper {...styles.mainContainer}>
            <StyledWrapper {...styles.container}>
                <TextField
                    {...styles.inputStyles}
                    label={translate("Name*")}
                    placeholder={translate("First and Last Name")}
                    value={fullname}
                    error={isSubmitted && fullnameError}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => fullnameChangeHandler(e)}
                />
                <TextField
                    {...styles.inputStyles}
                    label={translate("Company*")}
                    placeholder={translate("Company")}
                    value={companyName}
                    error={isSubmitted && companyError}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => companyHandler(e)}
                />
                <TextField
                    {...styles.inputStyles}
                    label={translate("Your Website")}
                    placeholder={translate("Your Website")}
                    value={websiteName}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => websiteNameChangeHander(e)}
                />
                <StyledWrapper {...styles.dFlexRowStyles}>
                    <TextField
                        {...styles.inputStyles}
                        label={translate("Email*")}
                        placeholder={translate("Email")}
                        value={emailAddress}
                        error={isSubmitted && emailAddressError}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => emailChangeHandler(e)}
                    />
                    <TextField
                        {...styles.inputStyles}
                        label={translate("Phone Number*")}
                        placeholder={translate("Phone Number")}
                        value={phoneNumber}
                        error={isSubmitted && phoneNumberError}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => phoneNumberChangeHandler(e)}
                    />
                </StyledWrapper>

                <TextField
                    label={translate("Street Address")}
                    placeholder={translate("Street Address")}
                    {...styles.inputStyles}
                    value={streetAddress}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => streetAddressChangeHandler(e)}
                ></TextField>

                <StyledWrapper {...styles.dFlexRowStyles}>
                    <TextField
                        label={translate("City*")}
                        placeholder={translate("City")}
                        {...styles.inputStyles}
                        value={city}
                        error={isSubmitted && cityError}
                        onChange={(e: any) => cityHandler(e)}
                    ></TextField>
                    <Select
                        label={translate("State*")}
                        {...styles.selectStyles}
                        onChange={e => stateChangeHandler(e.currentTarget.value)}
                        error={isSubmitted && stateError}
                        value={countryState}
                    >
                        <option value="" selected>
                            Select State
                        </option>
                        {states?.map(state => (
                            <option value={state.abbreviation} key={state.id}>
                                {state.name}
                            </option>
                        ))}
                    </Select>
                </StyledWrapper>

                <TextField
                    label={translate("Zip Code*")}
                    placeholder={translate("Zip Code*")}
                    {...styles.inputStyles}
                    value={zipCode}
                    error={isSubmitted && zipCodeError}
                    maxLength={6}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => zipCodeHandler(e)}
                ></TextField>

                <Select
                    label={translate("Request Type*")}
                    {...styles.selectStyles}
                    value={requestType}
                    onChange={(e: React.ChangeEvent<HTMLSelectElement>) => requestTypeHandler(e)}
                    error={isSubmitted && requestTypeError}
                >
                    <option value="" selected>
                        Select Request Type
                    </option>
                    {fields.requestypes.map((item, index) => {
                        return (
                            <option value={item.fields.optionName} key={index}>
                                {item.fields.optionName}
                            </option>
                        );
                    })}
                </Select>

                <TextArea
                    label={translate("Message*")}
                    placeholder={translate("How can we help?")}
                    {...styles.textAreaStyles}
                    value={message}
                    onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => messageChangeHandler(e)}
                    error={isSubmitted && messageError}
                ></TextArea>

                <StyledWrapper {...styles.captchaStyles}>
                    <ReCaptcha location="ContactUs" recaptchaRef={recaptchaRef} />
                </StyledWrapper>

                <Button
                    {...styles.submitButton}
                    type="submit"
                    onClick={submitFormHandler}
                    disabled={isSubmitted && !isSubmitEnabled}
                >
                    {fields.submitButtonText}
                </Button>

                <Modal
                    headline={translate("Message Sent")}
                    contentLabel={fields.successMessage}
                    {...styles.successModal}
                    isOpen={isModalOpen}
                    handleClose={modalCloseHandler}
                >
                    <GridContainer {...styles.modalContainer}>
                        <GridItem {...styles.messageTextGridItem}>
                            <Typography {...styles.messageText}>{fields.successMessage}</Typography>
                        </GridItem>
                        <GridItem {...styles.buttonGridItem}>
                            <Button {...styles.continueButton} onClick={continueButtonClickHandler}>
                                {translate("Continue")}
                            </Button>
                        </GridItem>
                    </GridContainer>
                </Modal>
            </StyledWrapper>
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(CENInquiryLineCard),
    definition: {
        group: "Contact Us",
        icon: "inputStyles-pipe",
        displayName: "Inquiry and LineCard Form",
        fieldDefinitions: [
            {
                name: fields.requestypes,
                editorTemplate: "ListField",
                getDisplay: (item: HasFields) => {
                    return item.fields.optionName;
                },
                defaultValue: [],
                fieldType: "General",
                displayName: "Add Request Type Options",
                fieldDefinitions: [
                    {
                        name: fields.optionName,
                        editorTemplate: "TextField",
                        displayName: "Option Name",
                        defaultValue: "",
                    },
                ],
            },

            {
                name: fields.submitButtonText,
                displayName: "Submit Button Text",
                editorTemplate: "TextField",
                fieldType: "General",
                defaultValue: "Send",
            },
            {
                name: fields.successMessage,
                displayName: "Submit Success Message",
                editorTemplate: "RichTextField",
                fieldType: "Translatable",
                defaultValue: "Thank you for your inquiry. A customer service representative will contact you shortly.",
            },
        ],
    },
};

export default widgetModule;
